/**
 * useAuthSync Hook
 * Cross-tab authentication synchronization
 * 
 * This hook listens for auth events from other tabs and syncs the session.
 * It should be used once at the app root level (e.g., in App.jsx or a provider).
 * 
 * Features:
 * - Auto-sync login from other tabs
 * - Auto-logout when another tab logs out
 * - Request session from existing tabs on new tab open
 * - Respond to session requests from new tabs
 * 
 * @author Auth System
 * @version 1.0.0
 */

import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AuthChannelService, { AUTH_EVENTS } from '../services/authChannelService';
import TokenService from '../services/tokenService';
import { 
  selectIsAuthenticated,
  syncLogoutFromOtherTab,
  restoreSession
} from '../store/slices/authSlice';

// Debounce helper to prevent rapid-fire logout events
const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

/**
 * Hook for cross-tab auth synchronization
 * @param {Object} options - Configuration options
 * @param {boolean} options.requestSessionOnMount - Request session from other tabs on mount
 * @returns {Object} Sync status and utilities
 */
const useAuthSync = (options = {}) => {
  const { requestSessionOnMount = true } = options;
  
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isInitialized = useRef(false);
  const unsubscribeRef = useRef(null);
  
  // Use refs to avoid stale closures and prevent re-subscription loops
  const isAuthenticatedRef = useRef(isAuthenticated);
  const isHandlingLogoutRef = useRef(false);
  const lastLogoutTimestampRef = useRef(0);
  
  // Keep ref in sync with state
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  /**
   * Handle incoming auth messages from other tabs
   * Uses refs to prevent stale closures and avoid re-subscription loops
   */
  const handleAuthMessage = useCallback(({ type, payload, timestamp }) => {
    switch (type) {
      case AUTH_EVENTS.LOGIN:
      case AUTH_EVENTS.TOKEN_UPDATE:
        // Another tab logged in or refreshed token - apply session
        if (payload) {
          TokenService.applySessionFromBroadcast(payload);
          // Restore session in Redux
          dispatch(restoreSession());
        }
        break;
        
      case AUTH_EVENTS.LOGOUT:
        // Prevent logout loop - check if we're already handling a logout
        // or if this logout event is too close to the last one (within 2 seconds)
        const now = Date.now();
        if (isHandlingLogoutRef.current || (now - lastLogoutTimestampRef.current) < 2000) {
          return;
        }
        
        isHandlingLogoutRef.current = true;
        lastLogoutTimestampRef.current = now;
        
        // Another tab logged out - clear local session without broadcasting back
        TokenService.clearTokens({ broadcast: false });
        dispatch(syncLogoutFromOtherTab());
        
        // Reset the handling flag after a short delay
        setTimeout(() => {
          isHandlingLogoutRef.current = false;
        }, 500);
        break;
        
      case AUTH_EVENTS.SESSION_REQUEST:
        // New tab is asking for session - respond if we have one
        // Use ref to get current auth state without causing re-renders
        if (isAuthenticatedRef.current) {
          const sessionData = TokenService.getSessionData();
          if (sessionData) {
            AuthChannelService.respondWithSession(sessionData);
          }
        }
        break;
        
      case AUTH_EVENTS.SESSION_RESPONSE:
        // Received session from another tab - apply it
        // Use ref to check current auth state
        if (payload && !isAuthenticatedRef.current) {
          TokenService.applySessionFromBroadcast(payload);
          dispatch(restoreSession());
        }
        break;
        
      default:
        break;
    }
  }, [dispatch]); // Remove isAuthenticated from dependencies - use ref instead

  /**
   * Initialize channel subscription - runs only once
   */
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;
    
    // Subscribe to auth channel - only once
    unsubscribeRef.current = AuthChannelService.subscribe(handleAuthMessage);
    
    // Request session from other tabs if not authenticated
    if (requestSessionOnMount && !TokenService.isAuthenticated()) {
      AuthChannelService.requestSession();
    }
    
    // Cleanup on unmount
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      isInitialized.current = false;
    };
  }, []); // Empty dependency array - initialize only once

  // No need for separate effect to re-subscribe - we use refs now

  return {
    isInitialized: isInitialized.current,
    tabId: AuthChannelService.getTabId(),
  };
};

export default useAuthSync;
